import React from 'react';
import { Car, Bike } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEvents } from '../../hooks/useEvents';
import { useEventRegistration } from '../../hooks/useEventRegistration';
import { useEventFilters } from '../../hooks/useEventFilters';
import Pagination from '../../components/Pagination';
import EventCard from '../../components/events/EventCard';
import EventDetailsModal from '../../components/events/EventDetailsModal';
import RegistrationModal from '../../components/events/RegistrationModal';
import EventsHeader from '../../components/events/EventsHeader';
import NoEventsFound from '../../components/events/NoEventsFound';
import { isRegistrationOpen } from '../../utils/dateUtils';

export default function Events() {
  const { user, userProfile } = useAuth();
  
  // Main data and operations hook
  const {
    events,
    driverProfiles,
    registrations,
    loading,
    userRole,
    formatDateForDisplay,
    isEventUpcoming,
    getUserEventRegistrations,
    isUserRegisteredForEvent,
    getEventRegistrationCount,
    getEventTotalAmountToPay,
    fetchUserRegistrations,
    refreshRegistrations
  } = useEvents(user?.id);

  // Event registration hook
  const {
    selectedEvent,
    setSelectedEvent,
    selectedCategory,
    setSelectedCategory,
    showRegistrationModal,
    setShowRegistrationModal,
    selectedVehicleFilter,
    setSelectedVehicleFilter,
    isRegistering,
    includeToolCar,
    setIncludeToolCar,
    toolCarCount,
    setToolCarCount,
    handleEventClick,
    handleCategorySelect,
    handleRegistration,
    handleUnregister
  } = useEventRegistration(
    user?.id,
    userProfile?.email,
    userProfile?.full_name,
    registrations,
    driverProfiles,
    fetchUserRegistrations
  );

  // Filtering and pagination hook
  const {
    searchTerm,
    setSearchTerm,
    showUpcomingOnly,
    setShowUpcomingOnly,
    currentPage,
    setCurrentPage,
    filteredEvents,
    currentEvents,
    totalPages
  } = useEventFilters(events);

  // Helper function to get vehicle icon
  const getVehicleIcon = (vehicleType: string) => {
    return vehicleType === 'car' ? <Car className="h-4 w-4" /> : <Bike className="h-4 w-4" />;
  };

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top of events section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle ticket shop URL click
  const handleTicketShopClick = (url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isDriver = userRole === 'driver';

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with search and filters */}
        <EventsHeader 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showUpcomingOnly={showUpcomingOnly}
          setShowUpcomingOnly={setShowUpcomingOnly}
          filteredEvents={filteredEvents}
          totalEvents={events.length}
        />

        {filteredEvents.length === 0 ? (
          <NoEventsFound 
            searchTerm={searchTerm}
            showUpcomingOnly={showUpcomingOnly}
            clearSearch={() => setSearchTerm('')}
            showAllEvents={() => setShowUpcomingOnly(false)}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentEvents.map((event) => {
                const userRegistrations = getUserEventRegistrations(event.id);
                const isRegistered = isUserRegisteredForEvent(event.id);
                const registrationCount = getEventRegistrationCount(event.id);
                const eventIsUpcoming = isEventUpcoming(event.event_date);
                const regOpen = isRegistrationOpen(event.event_date);

                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    isRegistered={isRegistered}
                    registrationCount={registrationCount}
                    eventIsUpcoming={eventIsUpcoming}
                    registrationOpen={regOpen}
                    isDriver={isDriver}
                    formatDateForDisplay={formatDateForDisplay}
                    getVehicleIcon={getVehicleIcon}
                    userRegistrations={userRegistrations}
                    onClick={() => handleEventClick(event, userRole)}
                  />
                );
              })}
            </div>

            {/* Pagination */}
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={6}
              totalItems={filteredEvents.length}
            />
          </>
        )}

        {/* Event Details Modal (Driver Only) */}
        {selectedEvent && isDriver && (
          <EventDetailsModal
            selectedEvent={selectedEvent}
            isEventUpcoming={isEventUpcoming}
            isRegistrationOpen={isRegistrationOpen}
            formatDateForDisplay={formatDateForDisplay}
            selectedVehicleFilter={selectedVehicleFilter}
            setSelectedVehicleFilter={setSelectedVehicleFilter}
            registrations={registrations}
            handleCategorySelect={handleCategorySelect}
            handleUnregister={handleUnregister}
            getEventTotalAmountToPay={getEventTotalAmountToPay}
            onClose={() => setSelectedEvent(null)}
            getVehicleIcon={getVehicleIcon}
            handleTicketShopClick={handleTicketShopClick}
            refreshRegistrations={refreshRegistrations}
          />
        )}

        {/* Registration Confirmation Modal */}
        <RegistrationModal
          showRegistrationModal={showRegistrationModal}
          selectedCategory={selectedCategory}
          selectedEvent={selectedEvent}
          includeToolCar={includeToolCar}
          setIncludeToolCar={setIncludeToolCar}
          toolCarCount={toolCarCount}
          setToolCarCount={setToolCarCount}
          isRegistering={isRegistering}
          handleRegistration={handleRegistration}
          onClose={() => setShowRegistrationModal(false)}
          formatDateForDisplay={formatDateForDisplay}
        />
      </div>
    </div>
  );
}